import type { StoreCreateCustomer, StoreUpdateCustomer } from "@medusajs/types";
import { medusa } from "#/lib/medusa";
import type { Customer } from "#/lib/medusa-types/auth";
import { registerUserFn } from "#/server/functions/auth-email";

export type LoginInput = {
  email: string;
  password: string;
};

export type RegisterInput = {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
};

export type RequestPasswordResetInput = {
  email: string;
};

export type ResetPasswordInput = {
  email: string;
  password: string;
  token: string;
};

export type UnverifiedInfo = {
  customerId: string;
  email: string;
  name?: string;
};

export type LoginResult =
  | { kind: "ok"; customer: Customer }
  | { kind: "redirect"; location: string }
  | ({ kind: "unverified" } & UnverifiedInfo);

export type RegisterResult = UnverifiedInfo;

export function isEmailVerified(customer: Customer): boolean {
  return customer.metadata?.email_verified === true;
}

export function customerDisplayName(customer: Customer): string | undefined {
  const parts = [customer.first_name, customer.last_name].filter(
    (part): part is string => typeof part === "string" && part.length > 0,
  );
  return parts.length > 0 ? parts.join(" ") : undefined;
}

export async function login(input: LoginInput): Promise<LoginResult> {
  const result = await medusa.auth.login("customer", "emailpass", {
    email: input.email,
    password: input.password,
  });
  if (typeof result !== "string") {
    return { kind: "redirect", location: result.location };
  }

  const { customer } = await medusa.store.customer.retrieve({
    fields: "+metadata",
  });
  if (!isEmailVerified(customer)) {
    await medusa.auth.logout().catch(() => {});
    return {
      kind: "unverified",
      customerId: customer.id,
      email: customer.email,
      name: customerDisplayName(customer),
    };
  }
  return { kind: "ok", customer };
}

export async function register(input: RegisterInput): Promise<RegisterResult> {
  await medusa.auth.register("customer", "emailpass", {
    email: input.email,
    password: input.password,
  });

  const body: StoreCreateCustomer = {
    email: input.email,
    first_name: input.firstName ?? null,
    last_name: input.lastName ?? null,
    phone: input.phone ?? null,
  };

  let customer: Customer;
  try {
    const created = await medusa.store.customer.create(body);
    customer = created.customer;
  } catch (err) {
    await medusa.auth.logout().catch(() => {});
    throw err;
  }

  const name = customerDisplayName(customer);
  await registerUserFn({
    data: { email: input.email, customerId: customer.id, customerName: name },
  }).catch(() => {});

  await medusa.auth.logout().catch(() => {});

  return { customerId: customer.id, email: input.email, name };
}

export async function logout(): Promise<void> {
  await medusa.auth.logout();
}

export async function requestPasswordReset(
  input: RequestPasswordResetInput,
): Promise<void> {
  await medusa.auth.resetPassword("customer", "emailpass", {
    identifier: input.email,
  });
}

export async function resetPassword(input: ResetPasswordInput): Promise<void> {
  await medusa.auth.updateProvider(
    "customer",
    "emailpass",
    { email: input.email, password: input.password },
    input.token,
  );
}

export async function fetchCurrentCustomer(): Promise<Customer | null> {
  // Anonymous visitors (the vast majority of page loads) have no stored JWT,
  // so retrieving would always 401 — skip the request entirely rather than
  // logging a network error for an expected outcome.
  if (
    typeof window !== "undefined" &&
    !window.localStorage.getItem("medusa_auth_token")
  ) {
    return null;
  }
  try {
    const { customer } = await medusa.store.customer.retrieve({
      fields: "+metadata",
    });
    return customer;
  } catch {
    return null;
  }
}

export async function updateProfile(
  input: StoreUpdateCustomer,
): Promise<Customer> {
  const { customer } = await medusa.store.customer.update(input);
  return customer;
}

export type EmailChangeRequestInput = { new_email: string };
export type EmailChangeConfirmInput = { token: string; email: string };

// The medusa-side email-change routes are shipped in parallel by IA-medusa;
// until they land the storefront calls these endpoints via the raw client and
// surfaces a "coming soon" toast on the catch path (see callers).
export async function requestEmailChange(
  input: EmailChangeRequestInput,
): Promise<void> {
  await medusa.client.fetch("/store/customers/me/email-change-request", {
    method: "POST",
    body: input,
  });
}

export async function confirmEmailChange(
  input: EmailChangeConfirmInput,
): Promise<void> {
  await medusa.client.fetch("/store/customers/me/email-change-confirm", {
    method: "POST",
    body: input,
  });
}
